import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { affiliateApi, AffiliateProfile, AffiliateLink, Commission, AvailableProduct, DashboardStats, PaginatedResponse, Category } from '../services/api';

// ============================================
// QUERY KEYS
// ============================================

export const affiliateKeys = {
  all: ['affiliate'] as const,
  profile: () => [...affiliateKeys.all, 'profile'] as const,
  products: (storeId?: string) => [...affiliateKeys.all, 'products', storeId] as const,
  links: (params?: Record<string, unknown>) => [...affiliateKeys.all, 'links', params] as const,
  link: (id: string) => [...affiliateKeys.all, 'links', id] as const,
  commissions: (params?: Record<string, unknown>) => [...affiliateKeys.all, 'commissions', params] as const,
  dashboard: () => [...affiliateKeys.all, 'dashboard'] as const,
};

// ============================================
// PROFILE HOOKS
// ============================================

export function useAffiliateProfile(options?: UseQueryOptions<AffiliateProfile, Error>) {
  return useQuery<AffiliateProfile, Error>({
    queryKey: affiliateKeys.profile(),
    queryFn: () => affiliateApi.getProfile(),
    ...options,
  });
}

export function useRegisterAffiliate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof affiliateApi.registerAffiliate>[0]) =>
      affiliateApi.registerAffiliate(data),
    onSuccess: (data) => {
      queryClient.setQueryData(affiliateKeys.profile(), data);
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof affiliateApi.updateProfile>[0]) =>
      affiliateApi.updateProfile(data),
    onSuccess: (data) => {
      queryClient.setQueryData(affiliateKeys.profile(), data);
    },
  });
}

// PRODUCTS HOOKS
// ============================================

export function useAvailableProducts(params?: Parameters<typeof affiliateApi.getAvailableProducts>[0]) {
  return useQuery<PaginatedResponse<AvailableProduct>, Error>({
    queryKey: affiliateKeys.products(params ? JSON.stringify(params) : undefined),
    queryFn: () => affiliateApi.getAvailableProducts(params),
  });
}

export function useAvailableCategories(storeId?: string) {
  return useQuery<Category[], Error>({
    queryKey: ['affiliate', 'categories', storeId],
    queryFn: () => affiliateApi.getAvailableCategories(storeId),
  });
}

// ============================================
// LINKS HOOKS
// ============================================

export function useAffiliateLinks(params?: {
  page?: number;
  limit?: number;
  isActive?: boolean;
  productId?: string;
  sortBy?: 'createdAt' | 'totalClicks' | 'totalSales' | 'revenue';
  sortOrder?: 'asc' | 'desc';
}) {
  return useQuery<PaginatedResponse<AffiliateLink>, Error>({
    queryKey: affiliateKeys.links(params),
    queryFn: () => affiliateApi.getLinks(params),
    refetchInterval: 30000, // Refetch every 30 seconds for real-time stats updates
  });
}

export function useCreateLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof affiliateApi.createLink>[0]) =>
      affiliateApi.createLink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.links() });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.dashboard() });
    },
  });
}

export function useUpdateLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ linkId, data }: { linkId: string; data: Parameters<typeof affiliateApi.updateLink>[1] }) =>
      affiliateApi.updateLink(linkId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.links() });
    },
  });
}

export function useDeleteLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (linkId: string) => affiliateApi.deleteLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.links() });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.dashboard() });
    },
  });
}

// ============================================
// COMMISSIONS HOOKS
// ============================================

export function useCommissions(params?: {
  page?: number;
  limit?: number;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PAID';
  startDate?: string;
  endDate?: string;
  sortBy?: 'createdAt' | 'commissionAmount';
  sortOrder?: 'asc' | 'desc';
}) {
  return useQuery<PaginatedResponse<Commission>, Error>({
    queryKey: affiliateKeys.commissions(params),
    queryFn: () => affiliateApi.getCommissions(params),
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  });
}

// ============================================
// DASHBOARD HOOKS
// ============================================

export function useDashboardStats() {
  return useQuery<DashboardStats, Error>({
    queryKey: affiliateKeys.dashboard(),
    queryFn: () => affiliateApi.getDashboardStats(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
}
// ============================================
// PAYOUTS HOOKS
// ============================================

export function useInitiatePayout() {
  return useMutation({
    mutationFn: (data: Parameters<typeof affiliateApi.initiatePayout>[0]) => affiliateApi.initiatePayout(data),
  });
}

export function useRequestPayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof affiliateApi.requestPayout>[0]) => affiliateApi.requestPayout(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: affiliateKeys.commissions() });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.profile() });
      queryClient.invalidateQueries({ queryKey: affiliateKeys.dashboard() });
    },
  });
}
